// src/curated/dmc/dmcEditor.js
// DMC trips: a partner's (destination management company's) package, sold as
// RouteChef routes (getting there and back) + the DMC's hotel and cab for every day.
//
// Per-night model. Each day says where it starts and where you sleep:
//   day.starts_in  city the day starts in (Day 1: the arrival gateway's city)
//   day.night      city of that night's hotel (null on the last day)
// Hotel stays are never stored: they are runs of consecutive nights in the same place, so
// adding, removing or moving a day is a plain list edit and check-in / check-out dates follow.
//
// The traveller's version (state) only keeps day references ({ uid, id }); the day content
// always comes from the trip, so a partner's text edits reach travellers who saved a version.
//
// Everything here is pure (no React, no network): the page, the AI chat options, the partner
// editor preview and the tests all use the same functions.

import { applyOrigin } from "../originPlace";

const uidOf = (() => { let n = 0; return (p = "d") => `${p}${Date.now().toString(36)}${(++n).toString(36)}`; })();
const clone = (x) => JSON.parse(JSON.stringify(x));
const addDaysISO = (iso, n) => {
    const d = new Date(`${iso}T00:00:00`);
    d.setDate(d.getDate() + n);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const roundH = (h) => (Number.isInteger(h) ? `${h}` : h.toFixed(1).replace(/\.0$/, ""));

/* ── catalog lookups ─────────────────────────────────────────────────────── */

export const catalogDay = (trip, id) => (trip.days || []).find((d) => d.id === id) || (trip.day_pool || []).find((d) => d.id === id) || null;
export const tierOf = (trip, key) => (trip.tiers || []).find((t) => t.key === key) || (trip.tiers || []).find((t) => t.key === trip.default_tier) || (trip.tiers || [])[0] || null;
export const gatewayOf = (trip, key) => {
    const k = trip.gateway_aliases?.[key] || key;
    return (trip.gateways || []).find((g) => g.key === k) || (trip.gateways || [])[0] || null;
};
export const addonOf = (trip, id) => (trip.addons || []).find((a) => a.id === id) || null;

export function driveHours(trip, a, b) {
    if (!a || !b || a === b) return 0;
    const m = trip.drive_hours || {};
    const v = m[`${a}|${b}`] ?? m[`${b}|${a}`];
    return typeof v === "number" ? v : null;
}

/* ── state ──────────────────────────────────────────────────────────────── */

/** Fixed-date trips (trip.departures): the next departure from tomorrow, as YYYY-MM-DD, or "" */
export function nextDeparture(trip, after = null) {
    const d0 = after || (() => { const d = new Date(); d.setDate(d.getDate() + 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })();
    return ((trip?.departures || []).map((x) => x?.start).filter((x) => /^\d{4}-\d{2}-\d{2}$/.test(x || "") && x >= d0).sort()[0]) || "";
}

export function initialDmcState(trip, params = {}) {
    // A fixed-date trip opens on its next departure unless a departure date was asked for
    const dep = nextDeparture(trip);
    if (dep && !(trip.departures || []).some((x) => x.start === params.startDate)) params = { ...params, startDate: dep };
    return {
        v: 1,
        days: (trip.days || []).map((d) => ({ uid: d.id, id: d.id })),
        tier: trip.default_tier || trip.tiers?.[0]?.key || null,
        gatewayIn: trip.default_gateway_in || trip.gateways?.[0]?.key || null,
        gatewayOut: trip.default_gateway_out || trip.default_gateway_in || trip.gateways?.[0]?.key || null,
        houseboat: [],          // day uids whose night is on the houseboat
        addons: [],             // prebooked add-on ids
        vehicle: null,          // null = picked by group size
        routesOff: { out: false, ret: false },   // true = the traveller (or agent) arranges that journey themselves
        edits: {},              // day uid -> { title?, text?, blocks?, by } : places and events changed for this traveller
        adjust: null,           // { amount, note }: partner's price change for a custom plan (net, before margin and GST)
        catalog: null,          // partner's rate changes for this plan only: { tiers, vehicles, dmc_markup_pct } (see effectiveTrip)
        rooming: null,          // partner's room setup: { rooms, extra_beds, child_nobed } (null = worked out from travellers)
        cabCount: null,         // older saves: number of cabs of `vehicle` (cabs below replaces both)
        cabs: null,             // the cabs, one key per cab, each can differ: ["suv", "sedan"] (null = by group size)
        params: { originName: "", startDate: "", pax: 2, routePref: "best", ...params },
    };
}

/** Saved state from an older catalog: drop days that no longer exist, keep anchors in place. */
export function reconcileDmcState(trip, saved) {
    if (!saved || !Array.isArray(saved.days)) return null;
    const fresh = initialDmcState(trip, saved.params || {});
    const known = saved.days.filter((d) => d && catalogDay(trip, d.id));
    if (known.length < 2) return null;
    const uids = new Set();
    // Any day can be first or last now (the partner can move the arrival and departure days too)
    const days = known.map((d) => {
        let uid = d.uid || d.id;
        while (uids.has(uid)) uid = uidOf("p");
        uids.add(uid);
        return { uid, id: d.id };
    });
    return {
        ...fresh,
        days,
        tier: tierOf(effectiveTrip(trip, saved), saved.tier)?.key || fresh.tier,
        gatewayIn: gatewayOf(trip, saved.gatewayIn)?.key || fresh.gatewayIn,
        gatewayOut: gatewayOf(trip, saved.gatewayOut)?.key || fresh.gatewayOut,
        houseboat: (saved.houseboat || []).filter((u) => uids.has(u)),
        addons: (saved.addons || []).filter((id) => addonOf(trip, id)),
        vehicle: (effectiveTrip(trip, saved).cab?.vehicles || []).some((v) => v.key === saved.vehicle) ? saved.vehicle : null,
        routesOff: { out: !!saved.routesOff?.out, ret: !!saved.routesOff?.ret },
        edits: Object.fromEntries(Object.entries(saved.edits || {}).filter(([u]) => uids.has(u))),
        adjust: saved.adjust && Number(saved.adjust.amount) ? { amount: Number(saved.adjust.amount), note: String(saved.adjust.note || "") } : null,
        ...(saved.plan_from ? { plan_from: saved.plan_from } : {}),
        ...(saved.version?.id ? { version: saved.version } : {}),
        catalog: cleanCatalog(saved.catalog),
        rooming: cleanRooming(saved.rooming),
        cabCount: Number(saved.cabCount) > 0 ? Math.min(20, Math.round(Number(saved.cabCount))) : null,
        cabs: cleanCabs(effectiveTrip(trip, saved), saved.cabs),
    };
}

export const MAX_CABS = 10;
function cleanCabs(T, cabs) {
    if (!Array.isArray(cabs)) return null;
    const keys = new Set((T.cab?.vehicles || []).map((v) => v.key));
    const out = cabs.map(String).filter((k) => keys.has(k)).slice(0, MAX_CABS);
    return out.length ? out : null;
}

/* ── the plan: what each day looks like right now ───────────────────────── */

/**
 * planDays(trip, state) → one entry per day:
 *   { uid, id, n, data, startsIn, night, houseboat, isArrival, isDeparture, title, transfer, warnings, iso }
 * transfer: { from, to, hours } when the day starts somewhere other than where the content expects.
 */
export function planDays(trip, state) {
    const gin = gatewayOf(trip, state.gatewayIn);
    const gout = gatewayOf(trip, state.gatewayOut);
    const out = [];
    state.days.forEach((ref, i) => {
        const data = catalogDay(trip, ref.id);
        if (!data) return;
        const prev = out[out.length - 1];
        const isArrival = i === 0;
        const isDeparture = i === state.days.length - 1;
        const startsIn = isArrival ? gin?.city : prev?.night;
        // A departure day moved into the middle sleeps where it starts
        const night = isDeparture ? null : (data.night || startsIn || null);
        const warnings = [];
        let title = data.title;
        let transfer = null;

        if (isArrival) {
            const h = driveHours(trip, startsIn, night);
            if (startsIn && night && startsIn !== night) {
                title = `Arrive at ${gin.short || gin.city}, drive to ${night}`;
                transfer = { from: startsIn, to: night, hours: h };
            } else {
                title = `Arrive in ${night || gin?.city || "Kashmir"}`;
            }
            // A sightseeing day moved to Day 1 keeps its name after the arrival
            if (data.anchor !== "start" && data.title) title = `Arrive, then ${data.title}`;
        } else if (isDeparture) {
            const to = gout?.city;
            const h = driveHours(trip, startsIn, to);
            title = startsIn && to && startsIn !== to ? `${startsIn} to ${gout.short || to}, head home` : `Head home from ${gout?.short || to || startsIn}`;
            if (data.anchor !== "end" && data.title) title = `${data.title}, then head home`;
            if (startsIn !== to) transfer = { from: startsIn, to, hours: h };
            if (h != null && h >= 6) warnings.push(`About ${roundH(h)} hours to ${gout.short || to}. Pick an evening train or flight home.`);
        } else if (data.starts_in && startsIn && data.starts_in !== startsIn) {
            const h = driveHours(trip, startsIn, data.starts_in);
            transfer = { from: startsIn, to: data.starts_in, hours: h };
            if (data.day_trip) {
                const ht = driveHours(trip, startsIn, data.day_trip);
                warnings.push(ht != null
                    ? `${data.day_trip} is about ${roundH(ht)} hours each way from ${startsIn}. It works better on a day you wake up in ${data.starts_in}.`
                    : `This day is planned from ${data.starts_in}, and you wake up in ${startsIn} that morning.`);
            } else {
                warnings.push(h != null ? `Starts with a ${roundH(h)} hour drive from ${startsIn} to ${data.starts_in}.` : `Starts with a drive from ${startsIn} to ${data.starts_in}.`);
            }
        }
        const houseboat = !!night && (state.houseboat || []).includes(ref.uid) && night === trip.houseboat?.city;
        const ed = state.edits?.[ref.uid] || null;
        const shown = ed ? { ...data, ...(ed.text != null ? { text: ed.text } : {}), ...(ed.blocks ? { blocks: ed.blocks } : {}) } : data;
        out.push({
            uid: ref.uid, id: ref.id, n: i + 1, data: shown, original: data, startsIn, night, houseboat,
            isArrival, isDeparture, title: ed?.title || title, transfer, warnings, edit: ed,
            iso: state.params?.startDate ? addDaysISO(state.params.startDate, i) : null,
            movable: true,
        });
    });
    return out;
}

/** Hotel stays: runs of consecutive nights in the same place (houseboat nights are their own stay). */
export function stayRuns(trip, plan) {
    const runs = [];
    plan.forEach((d, i) => {
        if (!d.night) return;
        const key = `${d.night}|${d.houseboat ? "hb" : "h"}`;
        const last = runs[runs.length - 1];
        if (last && last.key === key && last.endIdx === i - 1) {
            last.nights += 1;
            last.endIdx = i;
            last.uids.push(d.uid);
        } else {
            runs.push({ key, city: d.night, houseboat: d.houseboat, startIdx: i, endIdx: i, nights: 1, uids: [d.uid] });
        }
    });
    return runs.map((r) => ({
        ...r,
        checkInIso: plan[r.startIdx]?.iso || null,
        checkOutIso: plan[r.startIdx]?.iso ? addDaysISO(plan[r.startIdx].iso, r.nights) : null,
        checkInDay: r.startIdx + 1,
        checkOutDay: r.startIdx + 1 + r.nights,
    }));
}

/** Hotel name for a stay under the current tier */
export function stayLabel(trip, state, run) {
    const T = effectiveTrip(trip, state);
    if (run.houseboat) return T.houseboat?.label || "Houseboat";
    return tierOf(T, state.tier)?.hotels?.[run.city] || `Hotel in ${run.city}`;
}

/* ── which pickup a train / flight route leaves you at ──────────────────── */

const isCabSeg = (seg) => /cab|taxi/i.test(`${seg?.mode || ""} ${seg?.segment_type || ""} ${seg?.type || ""}`) && !seg?.train_num && !seg?.flight_num;

/** The last (dir "in") or first (dir "out") train / flight / bus leg; first and last mile cabs are ignored,
 *  they're the DMC's job */
export function endLeg(route, dir = "in") {
    const segs = (route?.nodes || []).map((n) => n?.segment).filter((s) => s && !isCabSeg(s));
    return dir === "in" ? segs[segs.length - 1] || null : segs[0] || null;
}

/** Where the route ends (in) or starts (out): { name, code, gateway } (gateway null = not one of ours) */
export function routeEnd(trip, route, dir = "in") {
    const seg = endLeg(route, dir);
    if (!seg) return null;
    const name = dir === "in" ? seg.dest_name : seg.origin_name;
    const codes = (dir === "in" ? [seg.dest_code_iata, seg.dest_code] : [seg.origin_code_iata, seg.origin_code])
        .filter(Boolean).map((c) => String(c).toUpperCase());
    const g = (trip.gateways || []).find((x) => {
        const m = x.match || {};
        if (codes.some((c) => (m.codes || []).includes(c))) return true;
        return m.names ? new RegExp(m.names, "i").test(String(name || "")) : false;
    }) || null;
    return { name: name || codes[0] || "", code: codes[0] || "", gateway: g };
}

/** The gateway every route search goes to and from */
export const searchGateway = (trip) => gatewayOf(trip, trip.route_search_gateway || trip.gateways?.[0]?.key);

/** Times the route search needs: when Day 1 must start, when the last day can leave */
export function routeTimes(trip, state, plan = planDays(trip, state)) {
    const gin = gatewayOf(trip, state.gatewayIn);
    const gout = gatewayOf(trip, state.gatewayOut);
    const last = plan[plan.length - 1];
    const h = driveHours(trip, last?.startsIn, gout?.city);
    // Leave the hotel at 09:00 after breakfast, drive, then 90 minutes to spare at the station / airport
    const mins = 9 * 60 + Math.round((h ?? 3) * 60) + 90;
    const leaveAfter = `${String(Math.min(23, Math.floor(mins / 60))).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
    return { startBy: gin?.start_by || "13:00", leaveAfter, gin, gout };
}

export function seasonFor(trip, iso) {
    if (!iso) return null;
    const m = Number(iso.slice(5, 7));
    return (trip.seasons || []).find((s) => (s.months || []).includes(m)) || null;
}

/* ── vehicle and rooms ──────────────────────────────────────────────────── */

/** The cabs for this plan by group size: the smallest type that fits everyone, as many as needed */
export function autoCabs(trip, state) {
    const list = effectiveTrip(trip, state).cab?.vehicles || [];
    if (!list.length) return [];
    const pax = Math.max(1, state.params?.pax || 1);
    const v = list.find((x) => x.max_pax >= pax) || list[list.length - 1];
    return Array(Math.min(MAX_CABS, Math.max(1, Math.ceil(pax / Math.max(1, v.max_pax))))).fill(v.key);
}

/** "2 × Innova", "Innova + Etios or Dzire", "2 × Innova + Tempo Traveller" */
export function cabsLabel(cabs) {
    const groups = [];
    for (const v of cabs) { const g = groups.find((x) => x.v.key === v.key); if (g) g.n += 1; else groups.push({ v, n: 1 }); }
    return groups.map((g) => `${g.n > 1 ? `${g.n} × ` : ""}${g.v.label}`).join(" + ");
}

/**
 * The plan's cabs. Each cab can be a different type (state.cabs: one key per cab), else by group size.
 * { cabs: [vehicle...], keys, count, per_day (all cabs, per day), seats, label, custom, autoKeys,
 *   key, max_pax (first cab, for older callers) }
 */
export function vehicleFor(trip, state) {
    const T = effectiveTrip(trip, state);
    const list = T.cab?.vehicles || [];
    if (!list.length) return null;
    const byKey = Object.fromEntries(list.map((v) => [v.key, v]));
    const autoKeys = autoCabs(trip, state);
    let keys = (cleanCabs(T, state.cabs) || []);
    if (!keys.length && byKey[state.vehicle]) {          // older saves: one type, maybe a count
        const v = byKey[state.vehicle];
        const pax = Math.max(1, state.params?.pax || 1);
        keys = Array(Math.min(MAX_CABS, state.cabCount || Math.max(1, Math.ceil(pax / Math.max(1, v.max_pax))))).fill(v.key);
    }
    const custom = keys.length > 0;
    if (!custom) keys = autoKeys;
    const cabs = keys.map((k) => byKey[k]).filter(Boolean);
    return {
        cabs, keys: cabs.map((v) => v.key), count: cabs.length,
        per_day: cabs.reduce((s, v) => s + (v.per_day || 0), 0),
        seats: cabs.reduce((s, v) => s + (v.max_pax || 0), 0),
        label: cabsLabel(cabs), custom, autoKeys,
        key: cabs[0]?.key, max_pax: cabs[0]?.max_pax || 0,
    };
}

/** Rooms, extra beds and children without a bed: the partner's setup, else worked out from travellers */
export function roomingFor(trip, state) {
    const auto = roomsFor(trip, Math.max(1, state.params?.pax || 1));
    const r = state.rooming || {};
    return {
        rooms: r.rooms ?? auto.rooms, extraBeds: r.extra_beds ?? auto.extraBeds, childNoBed: r.child_nobed ?? 0,
        auto: { rooms: auto.rooms, extraBeds: auto.extraBeds, childNoBed: 0 }, custom: !!state.rooming,
    };
}

/* ── the partner's rate changes for one plan ────────────────────────────── */

const toInt = (v) => { const n = Math.round(Number(v)); return Number.isFinite(n) && n > 0 ? n : 0; };
const cityMap = (o, f) => Object.fromEntries(Object.entries(o || {}).map(([k, v]) => [String(k).trim(), f(v)]).filter(([k, v]) => k && v));
export const keyOf = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 30);

export function cleanTier(t, i = 0) {
    const label = String(t?.label || "").trim().slice(0, 30) || `Category ${i + 1}`;
    return {
        key: keyOf(t?.key || label) || `tier${i + 1}`, label, blurb: String(t?.blurb || "").slice(0, 160),
        hotels: cityMap(t?.hotels, (v) => String(v || "").trim().slice(0, 200)),
        room_pn: cityMap(t?.room_pn, toInt), extra_bed_pn: cityMap(t?.extra_bed_pn, toInt), child_nobed_pn: cityMap(t?.child_nobed_pn, toInt),
        // per-person package trips: price per person for the whole package, by sharing
        package_pp: Object.fromEntries(["double", "triple", "single", "child_nobed"].map((k) => [k, toInt(t?.package_pp?.[k])]).filter(([, v]) => v)),
    };
}
export function cleanVehicle(v, i = 0) {
    const label = String(v?.label || "").trim().slice(0, 60) || `Vehicle ${i + 1}`;
    return { key: keyOf(v?.key || label) || `v${i + 1}`, label, max_pax: Math.max(1, Math.min(60, toInt(v?.max_pax) || 4)), per_day: toInt(v?.per_day) };
}
function uniqueKeys(list) {
    const seen = new Set();
    return list.map((x) => { let k = x.key; while (seen.has(k)) k += "x"; seen.add(k); return { ...x, key: k }; });
}
function cleanCatalog(c) {
    if (!c || typeof c !== "object") return null;
    const out = {};
    if (Array.isArray(c.tiers) && c.tiers.length) out.tiers = uniqueKeys(c.tiers.slice(0, 8).map(cleanTier));
    if (Array.isArray(c.vehicles) && c.vehicles.length) out.vehicles = uniqueKeys(c.vehicles.slice(0, 8).map(cleanVehicle));
    if (c.dmc_markup_pct != null && c.dmc_markup_pct !== "") out.dmc_markup_pct = Math.max(0, Math.min(100, Number(c.dmc_markup_pct) || 0));
    if (c.markup_pct != null && c.markup_pct !== "") out.markup_pct = Math.max(0, Math.min(100, Number(c.markup_pct) || 0));   // RouteChef's (admin)
    return Object.keys(out).length ? out : null;
}
function cleanRooming(r) {
    if (!r || typeof r !== "object") return null;
    const n = (v, min, max) => Math.max(min, Math.min(max, Math.round(Number(v) || 0)));
    return { rooms: n(r.rooms, 1, 30), extra_beds: n(r.extra_beds, 0, 30), child_nobed: n(r.child_nobed, 0, 30) };
}

/** The trip with this plan's own rate changes (hotel categories, cabs, the partner's markup) on top.
 *  Plans without changes follow the trip, so a permanent rate change shows in them too. */
export function effectiveTrip(trip, state) {
    const c = state?.catalog;
    if (!c) return trip;
    return {
        ...trip,
        ...(c.tiers ? { tiers: c.tiers } : {}),
        ...(c.vehicles ? { cab: { ...(trip.cab || {}), vehicles: c.vehicles } } : {}),
        ...(c.dmc_markup_pct != null ? { dmc_markup_pct: c.dmc_markup_pct } : {}),
        ...(c.markup_pct != null ? { markup_pct: c.markup_pct } : {}),
    };
}

export function roomsFor(trip, pax) {
    const base = trip.room?.base_occupancy || 2;
    const max = trip.room?.max_occupancy || base;
    const rooms = Math.max(1, Math.ceil(pax / max));
    const extraBeds = Math.max(0, Math.min(pax - rooms * base, rooms * (max - base)));
    return { rooms, extraBeds };
}

/* ── pricing ────────────────────────────────────────────────────────────── */

/**
 * priceDmc(trip, state, { plan, routes })
 *   routes = { out: { fare, pending }, ret: { fare, pending }, arrivalNight: bool }  (optional)
 * Rate card numbers are the DMC's net rates. The traveller sees them with RouteChef's markup and
 * GST folded in; the partner view shows the net lines.
 */
export function priceDmc(trip0, state, { plan = planDays(trip0, state), routes = null } = {}) {
    const trip = effectiveTrip(trip0, state);
    const pax = Math.max(1, state.params?.pax || 1);
    const { rooms, extraBeds, childNoBed } = roomingFor(trip, state);
    const tier = tierOf(trip, state.tier);
    const runs = stayRuns(trip, plan);
    const net = [];
    let estimated = false;

    // Package priced per person (most brochures and festival tours): one line for the stay and what comes
    // with it, by how people share rooms. Prices are for trip.package_days; other lengths scale per night.
    if (trip.pricing === "per_person") {
        const pp = tier?.package_pp || {};
        const dbl = Number(pp.double) || 0;
        const baseDays = Math.max(2, Number(trip.package_days) || (trip0.days || []).length || plan.length);
        const inBeds = Math.max(0, pax - childNoBed);
        const onExtra = Math.min(extraBeds, inBeds);
        const rest = inBeds - onExtra;
        const singles = Math.max(0, Math.min(rooms * 2 - rest, rooms, rest));
        const doubles = rest - singles;
        const price = (k) => Number(pp[k]) || 0;
        let est = !dbl;
        const parts = [];
        let amount = 0;
        const add = (n, each, label, guessed) => {
            if (!n) return;
            amount += n * each; if (guessed) est = true;
            parts.push(`${n} × ₹${each.toLocaleString("en-IN")} ${label}`);
        };
        add(doubles, dbl, "double sharing");
        add(singles, price("single") || dbl, "single", !price("single"));
        add(onExtra, price("triple") || dbl, "triple sharing", !price("triple"));
        add(childNoBed, price("child_nobed"), "child no bed", !price("child_nobed") && childNoBed > 0);
        if (plan.length !== baseDays) {   // longer or shorter than the package: scaled per night, flagged
            amount = Math.round(amount * Math.max(1, plan.length - 1) / Math.max(1, baseDays - 1));
            est = true;
        }
        if (est) estimated = true;
        net.push({
            k: "package", kind: "stay",
            label: `${tier?.label ? `${tier.label} package` : "Package"}, ${plan.length} days`,
            detail: parts.join(" + ") + (plan.length !== baseDays ? `, adjusted from ${baseDays} days` : ""),
            amount, estimated: est,
        });
    }

    for (const r of trip.pricing === "per_person" ? [] : runs) {
        let pn, eb, cnb, est = false;
        if (r.houseboat) {
            pn = trip.houseboat?.room_pn?.[tier?.key];
            eb = trip.houseboat?.extra_bed_pn?.[tier?.key] || 0;
            cnb = trip.houseboat?.child_nobed_pn?.[tier?.key] || 0;
        } else {
            pn = tier?.room_pn?.[r.city];
            eb = tier?.extra_bed_pn?.[r.city] || 0;
            cnb = tier?.child_nobed_pn?.[r.city] || 0;
        }
        if (!pn) {   // a city the rate card doesn't cover: use the tier's highest rate, flagged
            const vals = Object.values(tier?.room_pn || {}).filter((v) => Number(v) > 0);
            pn = vals.length ? Math.max(...vals) : 0;
            est = true; estimated = true;
        }
        const amount = r.nights * (pn * rooms + eb * extraBeds + cnb * childNoBed);
        const inr = (x) => `₹${x.toLocaleString("en-IN")}`;
        net.push({
            k: `stay-${r.startIdx}`, kind: "stay",
            label: `${r.houseboat ? trip.houseboat?.label || "Houseboat" : `${tier?.label || ""} hotel, ${r.city}`}, ${r.nights}N`,
            detail: [
                `${rooms} room${rooms > 1 ? "s" : ""} × ${inr(pn)}`,
                extraBeds ? `${extraBeds} extra bed${extraBeds > 1 ? "s" : ""} × ${inr(eb)}` : "",
                childNoBed ? `${childNoBed} child${childNoBed > 1 ? "ren" : ""} no bed × ${inr(cnb)}` : "",
            ].filter(Boolean).join(" + ") + " a night",
            amount, estimated: est,
        });
    }

    const veh = vehicleFor(trip, state);
    if (veh) {
        net.push({
            k: "cab", kind: "cab",
            label: `${veh.label}, ${plan.length} days`,
            detail: new Set(veh.keys).size > 1
                ? `${veh.cabs.map((v) => `₹${v.per_day.toLocaleString("en-IN")}`).join(" + ")} a day`
                : `₹${(veh.cabs[0]?.per_day || 0).toLocaleString("en-IN")} a day${veh.count > 1 ? " each" : ""}`,
            amount: veh.per_day * plan.length,
        });
        const gin = gatewayOf(trip, state.gatewayIn), gout = gatewayOf(trip, state.gatewayOut);
        const sup = ((gin?.supplement_per_vehicle || 0) + (gout?.supplement_per_vehicle || 0)) * veh.count;
        if (sup) {
            const where = [gin?.supplement_per_vehicle ? `pickup at ${gin.short}` : null, gout?.supplement_per_vehicle ? `drop at ${gout.short}` : null].filter(Boolean).join(" and ");
            net.push({ k: "gw", kind: "cab", label: `Long drive for ${where}`, amount: sup });
        }
    }

    const places = new Set(plan.map((d) => d.data.place).filter(Boolean));
    const monthOf = state.params?.startDate ? Number(state.params.startDate.slice(5, 7)) : null;
    const relevant = (a) => places.has(a.place) && (!a.months || !monthOf || a.months.includes(monthOf));
    for (const id of state.addons || []) {
        const a = addonOf(trip, id);
        if (!a || !a.prebook || !relevant(a)) continue;
        const qty = a.unit === "pp" ? pax : (veh?.count || 1);
        net.push({ k: `ad-${a.id}`, kind: "addon", label: a.label, detail: `${qty} × ₹${a.approx.toLocaleString("en-IN")}`, amount: a.approx * qty });
    }
    if (state.adjust && Number(state.adjust.amount)) {
        net.push({ k: "adjust", kind: "custom", label: state.adjust.note || `Changes by ${trip.partner?.short_name || trip.partner?.name || "your travel partner"}`, amount: Math.round(Number(state.adjust.amount)) });
    }
    if (routes?.arrivalNight) {
        const rates = Object.values(tier?.room_pn || {}).filter((v) => Number(v) > 0);
        const pn = rates.length ? Math.max(...rates) : 2500;
        net.push({ k: "arrnight", kind: "stay", label: "Night before Day 1 near the arrival point", amount: pn * rooms, estimated: true });
        estimated = true;
    }

    const markupPct = Number(trip.markup_pct ?? 10);
    const gstPct = Number(trip.gst_pct ?? 5);
    // The partner's own margin goes on their net cost first, then RouteChef's markup, then GST
    const dmcPct = Math.max(0, Number(trip.dmc_markup_pct) || 0);
    const withDmc = (x) => Math.round(x * (1 + dmcPct / 100));
    const up = (x) => Math.round(withDmc(x) * (1 + markupPct / 100));
    const netTotal = net.reduce((s, l) => s + l.amount, 0);
    const payout = net.reduce((s, l) => s + withDmc(l.amount), 0);
    const lines = net.map((l) => ({ ...l, amount: up(l.amount) }));
    const sell = lines.reduce((s, l) => s + l.amount, 0);
    const gst = Math.round(sell * gstPct / 100);
    const pkg = sell + gst;

    const routeLines = [];
    if (routes?.out) routeLines.push({ k: "out", label: "Getting there", amount: routes.out.fare || 0, pending: !routes.out.fare });
    if (routes?.ret) routeLines.push({ k: "ret", label: "Getting back", amount: routes.ret.fare || 0, pending: !routes.ret.fare });
    const routesTotal = routeLines.reduce((s, l) => s + (l.amount || 0), 0);
    const total = pkg + routesTotal;

    const payLocal = (trip.addons || []).filter((a) => relevant(a) && !(a.prebook && (state.addons || []).includes(a.id)));

    return {
        pax, rooms, extraBeds, vehicle: veh, tier,
        lines, gstPct, gst, sell, packageTotal: pkg,
        packagePerPerson: Math.round(pkg / pax),
        routeLines, routesTotal,
        total, perPerson: Math.round(total / pax),
        net, netTotal, dmcMarkupPct: dmcPct, dmcMarkup: payout - netTotal, payout, markupPct, markup: sell - payout,
        rooming: { rooms, extraBeds, childNoBed },
        payLocal, estimated: estimated || routeLines.some((l) => l.pending),
        nights: runs.reduce((s, r) => s + r.nights, 0), days: plan.length,
    };
}

/** What a traveller pays for a net amount on this plan: partner markup, RouteChef markup, then GST */
export function sellOf(trip0, state, netAmount) {
    const trip = effectiveTrip(trip0, state);
    const dmc = Math.max(0, Number(trip.dmc_markup_pct) || 0), mk = Number(trip.markup_pct ?? 10), gst = Number(trip.gst_pct ?? 5);
    return Math.round(Math.round(Math.round(netAmount * (1 + dmc / 100)) * (1 + mk / 100)) * (1 + gst / 100));
}

/* ── edits ──────────────────────────────────────────────────────────────── */

function roomingForRaw(trip, state) {
    const r = roomingFor(trip, state);
    return { rooms: r.rooms, extra_beds: r.extraBeds, child_nobed: r.childNoBed };
}

const BLOCK_TYPES = ["STOP", "EXPERIENCE", "TIP"];
/** Tidy a day's item list. Items the traveller adds or changes are requests the partner confirms. */
function cleanBlocks(list, by, original) {
    const orig = new Map(original.map((b) => [b.id, b]));
    return list.filter((b) => b && String(b.name || "").trim()).slice(0, 25).map((b) => {
        const o = orig.get(b.id);
        const nb = {
            id: b.id || uidOf("b"),
            type: BLOCK_TYPES.includes(b.type) ? b.type : "STOP",
            name: String(b.name).trim().slice(0, 120),
            ...(b.time && /^\d{2}:\d{2}$/.test(b.time) ? { time: b.time } : {}),
            ...(String(b.note || "").trim() ? { note: String(b.note).trim().slice(0, 300) } : {}),
            ...(b.addon ? { addon: b.addon } : {}),
            ...(b.included ? { included: true } : {}),
        };
        const changed = !o || o.name !== nb.name || (o.time || "") !== (nb.time || "") || (o.note || "") !== (nb.note || "");
        // Who changed it. The editor sets _dirty on items touched in this edit; untouched ones keep their mark.
        // When the partner saves a day, the traveller's requests on it count as accepted.
        if (changed) nb.by = by === "partner" ? "partner" : (b._dirty || !b.by ? "user" : b.by);
        return nb;
    });
}
function sameAsOriginal(e, data) {
    if (e.title || (e.text != null && e.text !== (data.text || ""))) return false;
    if (!e.blocks) return true;
    const strip = (bs) => JSON.stringify((bs || []).map((b) => [b.id, b.name, b.time || "", b.note || ""]));
    return strip(e.blocks) === strip(data.blocks);
}
/** Items taken out of a day compared with the partner's standard plan */
export function removedBlocks(d) {
    if (!d.edit?.blocks) return [];
    const ids = new Set(d.edit.blocks.map((b) => b.id));
    return (d.original.blocks || []).filter((b) => !ids.has(b.id));
}

const dayCountLimits = (trip) => ({ min: Math.max(2, trip.min_days || 3), max: Math.min(30, trip.max_days || 12) });

/** Where a new day fits best: right after the last night in the city it starts from */
function bestInsertIndex(trip, state, poolDay) {
    const plan = planDays(trip, state);
    let at = plan.length - 1;   // before departure by default
    for (let i = plan.length - 2; i >= 0; i--) {
        if (plan[i].night === poolDay.starts_in) { at = i + 1; break; }
    }
    return Math.max(1, Math.min(at, plan.length - 1));
}

/** The day "+" adds: an unused pool day from the main base city, else a slow day there */
export function defaultPoolDay(trip, state) {
    const plan = planDays(trip, state);
    const nights = {};
    plan.forEach((d) => { if (d.night) nights[d.night] = (nights[d.night] || 0) + 1; });
    const base = Object.entries(nights).sort((a, b) => b[1] - a[1])[0]?.[0];
    const used = new Set(state.days.map((d) => d.id));
    const pool = (trip.day_pool || []).filter((p) => p.starts_in === base && p.night === base);
    return pool.find((p) => !used.has(p.id) && !p.day_trip) || pool.find((p) => !used.has(p.id)) || pool[0] || (trip.day_pool || [])[0] || null;
}

/** The day "−" removes: the last day you added, else the latest day whose removal keeps the route sensible */
export function defaultRemovable(trip, state) {
    const plan = planDays(trip, state);
    const middle = plan.filter((d, i) => i > 0 && i < plan.length - 1);
    const added = middle.filter((d) => (trip.day_pool || []).some((p) => p.id === d.id));
    if (added.length) return added[added.length - 1];
    const keepsRoute = (d) => {
        const i = plan.indexOf(d);
        const prev = plan[i - 1], next = plan[i + 1];
        return !next || next.isDeparture || next.data.starts_in === prev.night;
    };
    const ok = middle.filter(keepsRoute);
    const nonTransfer = ok.filter((d) => d.data.night === d.data.starts_in);
    return nonTransfer[nonTransfer.length - 1] || ok[ok.length - 1] || middle[middle.length - 1] || null;
}

/**
 * applyDmcOps(trip, state, ops) → { state, changes, rejected, ui }
 * ops:
 *   { op: "add_day", pool_id?, at? }       at = index to insert at (1 … days-1)
 *   { op: "remove_day", uid? }              default: defaultRemovable
 *   { op: "move_day", uid, to }             to = target index (anchors stay put)
 *   { op: "set_days", count }
 *   { op: "set_tier", tier }
 *   { op: "set_gateway", in?, out? }
 *   { op: "toggle_houseboat", uid? }        default: the first Srinagar night
 *   { op: "toggle_addon", id }
 *   { op: "set_vehicle", key, count? }      count cabs of one type (null = by group size)
 *   { op: "set_cabs", cabs }                one key per cab, each can differ: ["suv", "sedan"] (null = by group size)
 *   { op: "set_routes", out?, ret? }        true = RouteChef books that journey, false = they arrange it
 *   { op: "set_params", origin?, origin_place?, start_date?, pax?, route_pref? }   origin_place: { name, lat, lng, code? } from the autocomplete
 *   { op: "edit_day", uid, title?, text?, blocks?, by }   places and events on a day; by "user" = a request
 *   { op: "reset_day", uid }                back to the partner's standard day
 *   { op: "set_adjust", amount, note }      partner only: net price change for this traveller's plan
 *   { op: "set_catalog", catalog | clear }  partner only: new hotel categories, cab types, rates, markup
 *
 * Travellers can pick anything the trip offers (days, hotel category, cab type and count, rooms).
 * Creating categories or cab types and changing costs (set_catalog, set_adjust) needs partner: true,
 * which the page passes only for the trip's DMC owner or an admin. RouteChef's own markup in a
 * catalog (markup_pct) also needs admin: true.
 *   { op: "open_human" }                    UI only
 */
export const PARTNER_ONLY_OPS = ["set_catalog", "set_adjust"];

export function applyDmcOps(trip, state, ops = [], { origins = [], partner = false, admin = false } = {}) {
    let next = clone(state);
    const changes = [], rejected = [], ui = [];
    const lim = dayCountLimits(trip);

    for (const o of ops) {
        if (!partner && PARTNER_ONLY_OPS.includes(o.op)) { rejected.push("Only the trip's partner can change rates or add categories."); continue; }
        switch (o.op) {
            case "add_day": {
                if (next.days.length >= lim.max) { rejected.push(`${lim.max} days is the longest we plan online. Chat with us for longer trips.`); break; }
                const pool = o.pool_id ? (trip.day_pool || []).find((p) => p.id === o.pool_id) : defaultPoolDay(trip, next);
                if (!pool) { rejected.push("There's no extra day to add for this trip."); break; }
                const at = o.at == null ? bestInsertIndex(trip, next, pool) : Math.max(0, Math.min(Number(o.at), next.days.length));
                const uid = uidOf("p");
                next.days.splice(at, 0, { uid, id: pool.id });
                const plan = planDays(trip, next);
                const d = plan.find((x) => x.uid === uid);
                changes.push(`Added Day ${d.n}: ${pool.title}${pool.night ? ` (+1 night in ${pool.night})` : ""}`);
                break;
            }
            case "remove_day": {
                if (next.days.length <= lim.min) { rejected.push(`${lim.min} days is the shortest version of this trip.`); break; }
                const target = o.uid ? planDays(trip, next).find((d) => d.uid === o.uid) : defaultRemovable(trip, next);
                if (!target) { rejected.push("There's no day I can take out."); break; }
                const label = `Day ${target.n} (${target.title})`;
                next.days = next.days.filter((d) => d.uid !== target.uid);
                next.houseboat = (next.houseboat || []).filter((u) => u !== target.uid);
                if (next.edits?.[target.uid]) { next.edits = { ...next.edits }; delete next.edits[target.uid]; }
                changes.push(`Removed ${label}${target.night ? `, one night less in ${target.night}` : ""}`);
                break;
            }
            case "move_day": {
                const from = next.days.findIndex((d) => d.uid === o.uid);
                if (from < 0) { rejected.push("I couldn't find that day."); break; }
                const to = Math.max(0, Math.min(Number(o.to), next.days.length - 1));
                if (to === from) break;
                const title = planDays(trip, next).find((x) => x.uid === o.uid)?.title || "that day";
                const [d] = next.days.splice(from, 1);
                next.days.splice(to, 0, d);
                changes.push(`Moved ${title} from Day ${from + 1} to Day ${to + 1}`);
                break;
            }
            case "set_days": {
                const want = Math.max(lim.min, Math.min(lim.max, Number(o.count) || next.days.length));
                for (let guard = 0; guard < 30 && next.days.length !== want; guard++) {
                    const r = applyDmcOps(trip, next, [{ op: next.days.length < want ? "add_day" : "remove_day" }], { origins });
                    if (!r.changes.length) { rejected.push(...r.rejected); break; }
                    next = r.state; changes.push(...r.changes);
                }
                break;
            }
            case "set_tier": {
                const t = tierOf(effectiveTrip(trip, next), o.tier);
                if (!t || t.key !== o.tier) { rejected.push("That hotel category isn't offered on this trip."); break; }
                if (next.tier === t.key) break;
                next.tier = t.key;
                changes.push(`Hotels: ${t.label}`);
                break;
            }
            case "set_gateway": {
                for (const side of ["in", "out"]) {
                    if (!o[side]) continue;
                    const g = gatewayOf(trip, o[side]);
                    if (!g || g.key !== (trip.gateway_aliases?.[o[side]] || o[side])) { rejected.push("We don't pick up or drop there."); continue; }
                    const k = side === "in" ? "gatewayIn" : "gatewayOut";
                    if (next[k] === g.key) continue;
                    next[k] = g.key;
                    changes.push(side === "in" ? `Pickup at ${g.label}` : `Drop at ${g.label}`);
                }
                break;
            }
            case "toggle_houseboat": {
                if (!trip.houseboat) { rejected.push("This trip has no houseboat option."); break; }
                const plan = planDays(trip, next);
                const d = o.uid ? plan.find((x) => x.uid === o.uid) : (plan.find((x) => (next.houseboat || []).includes(x.uid)) || plan.find((x) => x.night === trip.houseboat.city));
                if (!d || d.night !== trip.houseboat.city) { rejected.push(`Houseboat nights are only in ${trip.houseboat.city}.`); break; }
                const on = (next.houseboat || []).includes(d.uid);
                next.houseboat = on ? next.houseboat.filter((u) => u !== d.uid) : [...(next.houseboat || []), d.uid];
                changes.push(on ? `Day ${d.n} night back at the hotel` : `Day ${d.n} night on a ${trip.houseboat.label}`);
                break;
            }
            case "toggle_addon": {
                const a = addonOf(trip, o.id);
                if (!a || !a.prebook) { rejected.push("That one is paid on the spot, nothing to add."); break; }
                const on = (next.addons || []).includes(a.id);
                next.addons = on ? next.addons.filter((x) => x !== a.id) : [...(next.addons || []), a.id];
                changes.push(on ? `Removed ${a.label}` : `Added ${a.label}`);
                break;
            }
            case "set_vehicle": {
                const v = o.key ? (effectiveTrip(trip, next).cab?.vehicles || []).find((x) => x.key === o.key) : null;
                if (o.key && !v) { rejected.push("We don't have that vehicle."); break; }
                next.vehicle = null; next.cabCount = null;
                if (!v) { next.cabs = null; changes.push("Cabs picked by group size"); break; }
                const pax = Math.max(1, next.params?.pax || 1);
                const n = Number(o.count) > 0 ? Math.min(MAX_CABS, Math.round(Number(o.count))) : Math.min(MAX_CABS, Math.max(1, Math.ceil(pax / Math.max(1, v.max_pax))));
                next.cabs = Array(n).fill(v.key);
                changes.push(`Cab: ${vehicleFor(trip, next).label}`);
                break;
            }
            case "set_cabs": {
                next.vehicle = null; next.cabCount = null;
                next.cabs = cleanCabs(effectiveTrip(trip, next), o.cabs);
                if (o.cabs && !next.cabs) { rejected.push("We don't have that cab."); break; }
                changes.push(next.cabs ? `Cabs: ${vehicleFor(trip, next).label}` : "Cabs picked by group size");
                break;
            }
            case "set_catalog": {
                // partner only: { tiers?, vehicles?, dmc_markup_pct? } for this plan. null clears (back to the trip's own).
                if (o.clear) { if (next.catalog) { next.catalog = null; changes.push("Rates back to the trip's own"); } break; }
                const incoming = { ...(o.catalog || {}) };
                if (!admin) delete incoming.markup_pct;
                const merged = cleanCatalog({ ...(next.catalog || {}), ...incoming });
                next.catalog = merged;
                const T = effectiveTrip(trip, next);
                if (!tierOf(T, next.tier) || tierOf(T, next.tier).key !== next.tier) next.tier = T.tiers?.[0]?.key || next.tier;
                if (next.vehicle && !(T.cab?.vehicles || []).some((v) => v.key === next.vehicle)) next.vehicle = null;
                next.cabs = cleanCabs(T, next.cabs);
                changes.push(o.label || "Rates updated for this plan");
                break;
            }
            case "set_rooming": {
                next.rooming = o.auto ? null : cleanRooming({ ...roomingForRaw(trip, next), ...o });
                const r = roomingFor(trip, next);
                changes.push(o.auto ? "Rooms worked out from travellers" : `${r.rooms} room${r.rooms > 1 ? "s" : ""}${r.extraBeds ? `, ${r.extraBeds} extra bed${r.extraBeds > 1 ? "s" : ""}` : ""}${r.childNoBed ? `, ${r.childNoBed} child no bed` : ""}`);
                break;
            }
            case "set_routes": {
                const g = { out: gatewayOf(trip, next.gatewayIn), ret: gatewayOf(trip, next.gatewayOut) };
                next.routesOff = { out: false, ret: false, ...(next.routesOff || {}) };
                for (const leg of ["out", "ret"]) {
                    if (typeof o[leg] !== "boolean") continue;
                    const off = !o[leg];
                    if (next.routesOff[leg] === off) continue;
                    next.routesOff[leg] = off;
                    const where = leg === "out" ? `to ${g.out?.short || "the pickup"}` : `back from ${g.ret?.short || "the drop"}`;
                    changes.push(off ? `You'll arrange travel ${where} yourself` : `RouteChef finds travel ${where}`);
                }
                break;
            }
            case "set_params": {
                if (o.origin || o.origin_place) {
                    // Any city, airport or station picked in the box (origin_place), or a known city by name
                    const r = applyOrigin(next.params, o, origins);
                    if (r.error) rejected.push(`We couldn't find ${o.origin}. Pick it from the "Travelling from" box.`);
                    else if (r.changed) changes.push(`Starting from ${r.name}`);
                }
                if (o.start_date && /^\d{4}-\d{2}-\d{2}$/.test(o.start_date) && o.start_date !== next.params.startDate) {
                    next.params.startDate = o.start_date; changes.push(`Day 1 on ${o.start_date}`);
                }
                if (o.pax) {
                    const p = Math.max(1, Math.min(trip.max_group || 12, Number(o.pax)));
                    if (p !== next.params.pax) { next.params.pax = p; changes.push(`${p} traveller${p > 1 ? "s" : ""}`); }
                }
                if (o.route_pref && o.route_pref !== next.params.routePref) { next.params.routePref = o.route_pref; changes.push(`Routes: ${o.route_pref}`); }
                break;
            }
            case "edit_day": {
                // { uid, title?, text?, blocks?, by: "partner" | "user" }: blocks replace the day's list
                const d = planDays(trip, next).find((x) => x.uid === o.uid);
                if (!d) { rejected.push("I couldn't find that day."); break; }
                const by = o.by === "partner" ? "partner" : "user";
                const cur = next.edits?.[o.uid] || {};
                const e = { ...cur, by: cur.by === "partner" || by === "partner" ? "partner" : "user" };
                if (by === "partner" && typeof o.title === "string") e.title = o.title.trim().slice(0, 120) || undefined;
                if (by === "partner" && typeof o.text === "string") e.text = o.text.slice(0, 1500);
                if (Array.isArray(o.blocks)) e.blocks = cleanBlocks(o.blocks, by, d.original.blocks || []);
                next.edits = { ...(next.edits || {}), [o.uid]: e };
                if (sameAsOriginal(e, d.original)) delete next.edits[o.uid];
                changes.push(`Updated Day ${d.n}${by === "user" ? ` (${trip.partner?.short_name || "your partner"} confirms)` : ""}`);
                break;
            }
            case "reset_day": {
                if (!next.edits?.[o.uid]) break;
                const d = planDays(trip, next).find((x) => x.uid === o.uid);
                next.edits = { ...next.edits };
                delete next.edits[o.uid];
                changes.push(`Day ${d?.n || ""} back to the original plan`);
                break;
            }
            case "set_adjust": {
                const amount = Math.round(Number(o.amount) || 0);
                const note = String(o.note || "").slice(0, 120);
                const was = next.adjust;
                next.adjust = amount ? { amount, note } : null;
                if (JSON.stringify(was) !== JSON.stringify(next.adjust)) changes.push(amount ? `Price change: ${amount > 0 ? "+" : "−"}₹${Math.abs(amount).toLocaleString("en-IN")} net${note ? `, ${note}` : ""}` : "Price change removed");
                break;
            }
            case "open_human":
                ui.push(o);
                break;
            default:
                rejected.push("I can't do that one yet.");
        }
    }
    return { state: next, changes, rejected, ui };
}

/**
 * The partner's plan as the trip's new standard: day order, edited days, rate changes. Used by
 * "Make permanent" on the trip page. Returns a full trip doc to save.
 */
export function bakePlan(trip, state) {
    const T = effectiveTrip(trip, state);
    const plan = planDays(trip, state);
    const used = new Set();
    const strip = (bs) => (bs || []).map(({ by, _dirty, ...b }) => b); // eslint-disable-line no-unused-vars
    const days = plan.map((d, i) => {
        const src = catalogDay(trip, d.id);
        let id = src.id;
        while (used.has(id)) id = `${src.id}-${i + 1}`;
        used.add(id);
        const out = {
            ...src, id,
            title: d.edit?.title || src.title,
            text: d.data.text || "",
            blocks: strip(d.data.blocks),
            starts_in: i === 0 ? null : plan[i - 1].night,
            night: i === plan.length - 1 ? null : d.night,
        };
        delete out.anchor;
        if (i === 0) out.anchor = "start";
        if (i === plan.length - 1) out.anchor = "end";
        return out;
    });
    const { catalog, ...rest } = T; // eslint-disable-line no-unused-vars
    return {
        ...rest,
        days,
        default_tier: state.tier || T.default_tier,
        default_gateway_in: state.gatewayIn || T.default_gateway_in,
        default_gateway_out: state.gatewayOut || T.default_gateway_out,
    };
}

/** What a saved version is compared on: changed since it was saved or opened = these differ */
export function planSignature(state) {
    const { days, tier, gatewayIn, gatewayOut, houseboat, addons, vehicle, routesOff, edits, adjust, params, catalog, rooming, cabCount, cabs } = state;
    return JSON.stringify([days.map((d) => d.id), tier, gatewayIn, gatewayOut, houseboat, addons, vehicle, routesOff, edits, adjust, params?.pax, params?.startDate, catalog || null, rooming || null, cabCount || null, cabs || null]);
}

/* ── for chat, admin and the booking request ────────────────────────────── */

export function dmcSnapshot(trip, state, price = null, extra = {}) {
    const plan = planDays(trip, state);
    const runs = stayRuns(trip, plan);
    return {
        kind: "dmc",
        slug: trip.slug,
        title: trip.title,
        partner: trip.partner?.agent_id || null,
        origin: state.params?.originName || null,
        start_date: state.params?.startDate || null,
        pax: state.params?.pax || null,
        tier: tierOf(effectiveTrip(trip, state), state.tier)?.label || null,
        pickup: gatewayOf(trip, state.gatewayIn)?.label || null,
        drop: gatewayOf(trip, state.gatewayOut)?.label || null,
        days: plan.map((d) => ({
            n: d.n, date: d.iso, title: d.title, night: d.night, houseboat: d.houseboat || undefined,
            ...(d.edit ? {
                changed_by: d.edit.by,
                items: (d.data.blocks || []).map((b) => `${b.time ? `${b.time} ` : ""}${b.name}${b.by ? ` (${b.by === "user" ? "asked" : "changed"})` : ""}`),
                removed: removedBlocks(d).map((b) => b.name),
            } : {}),
        })),
        adjust: state.adjust || undefined,
        stays: runs.map((r) => ({ city: r.city, nights: r.nights, houseboat: r.houseboat || undefined, check_in: r.checkInIso, check_out: r.checkOutIso, hotel: stayLabel(trip, state, r) })),
        addons: (state.addons || []).map((id) => addonOf(trip, id)?.label).filter(Boolean),
        own_travel: { there: !!state.routesOff?.out, back: !!state.routesOff?.ret },
        vehicle: price?.vehicle ? price.vehicle.label : null,
        price: price ? { total: price.total, per_person: price.perPerson, package: price.packageTotal, routes: price.routesTotal, estimated: !!price.estimated } : null,
        edited: JSON.stringify(state.days.map((d) => d.id)) !== JSON.stringify((trip.days || []).map((d) => d.id)) || Object.keys(state.edits || {}).length > 0,
        requests: Object.values(state.edits || {}).some((e) => e.by === "user" || (e.blocks || []).some((b) => b.by === "user")),
        version_name: state.version?.name || undefined,
        // The full plan, so the partner can open it, change it and send it back
        state: { ...state, plan_from: undefined, version: undefined },
        ...extra,
    };
}

/** Plain text of the traveller's version, for the partner's inbox and WhatsApp */
export function snapshotText(s) {
    if (!s) return "";
    if (s.kind === "package") {   // an operator listing (Hornbill): no day plan, just what was asked about
        return [`${s.title} · ${s.duration || ""} · ${s.operator || ""}`, `${s.pax || "?"} traveller(s)${s.origin ? ` · from ${s.origin}` : ""}${s.start_date ? ` · from ${s.start_date}` : ""}`,
            `Listed price: ₹${Math.round(s.price || 0).toLocaleString("en-IN")} ${s.unit || ""}`, s.page_url || ""].filter(Boolean).join("\n");
    }
    const lines = [
        `${s.title}${s.version_name ? ` · version "${s.version_name}"` : ""}`,
        `${s.pax || "?"} traveller(s) · from ${s.origin || "?"} · Day 1 ${s.start_date || "?"}`,
        `${s.tier || ""} hotels · pickup ${s.pickup || "?"} · drop ${s.drop || "?"}`,
        ...(s.own_travel?.there || s.own_travel?.back ? [`Arranges own travel: ${[s.own_travel.there && "getting there", s.own_travel.back && "getting back"].filter(Boolean).join(" and ")}`] : []),
        ...s.days.flatMap((d) => [
            `Day ${d.n}${d.date ? ` (${d.date})` : ""}: ${d.title}${d.night ? ` · night in ${d.night}${d.houseboat ? " (houseboat)" : ""}` : ""}`,
            ...(d.items ? [`   ${d.items.join(", ")}${d.removed?.length ? ` · taken out: ${d.removed.join(", ")}` : ""}`] : []),
        ]),
    ];
    if (s.addons?.length) lines.push(`Add-ons: ${s.addons.join(", ")}`);
    if (s.adjust?.amount) lines.push(`Price change: ₹${Math.round(s.adjust.amount).toLocaleString("en-IN")} net${s.adjust.note ? ` (${s.adjust.note})` : ""}`);
    if (s.price) lines.push(`Shown price: ₹${Math.round(s.price.total).toLocaleString("en-IN")} (₹${Math.round(s.price.per_person).toLocaleString("en-IN")} pp)`);
    return lines.join("\n");
}

/* ── partner editor: checks before submitting a trip ────────────────────── */

export function validateDmcTrip(trip) {
    const errors = [];
    if (!trip?.title?.trim()) errors.push("Add a title.");
    if (!/^[a-z0-9-]{3,80}$/.test(trip?.slug || "")) errors.push("The link name can only have lowercase letters, numbers and dashes.");
    const days = trip?.days || [];
    if (days.length < 2) errors.push("A trip needs at least an arrival day and a departure day.");
    if (days.length) {
        if (days[days.length - 1].night) errors.push("The last day is the journey home, it shouldn't have a night.");
        days.slice(0, -1).forEach((d, i) => { if (!d.night) errors.push(`Day ${i + 1} needs a night city.`); });
    }
    const cities = new Set(days.map((d) => d.night).filter(Boolean));
    const perPerson = trip?.pricing === "per_person";
    if (!(trip?.tiers || []).length) errors.push(perPerson ? "Add at least one category with a price per person." : "Add at least one hotel category with rates.");
    for (const t of trip?.tiers || []) {
        if (perPerson && !(Number(t.package_pp?.double) > 0)) errors.push(`${t.label || "A category"}: price per person on double sharing is missing.`);
        for (const c of cities) {
            if (!perPerson && !(Number(t.room_pn?.[c]) > 0)) errors.push(`${t.label || "A category"}: room rate for ${c} is missing.`);
            if (!t.hotels?.[c]) errors.push(`${t.label || "A category"}: hotel name for ${c} is missing.`);
        }
    }
    // Shared transfers or none (package trips) need no vehicles; a private cab needs at least one
    const privateCab = (trip?.transfers || "private") === "private";
    if (privateCab && !(trip?.cab?.vehicles || []).length) errors.push("Add at least one vehicle with a day rate.");
    if (!(trip?.gateways || []).length) errors.push("Add at least one pickup point.");
    // Latitude and longitude are optional: points without them get a location from the airport and
    // station lists by name (gatewayCoords.js). A point still needs a name or town to be found by.
    for (const g of trip?.gateways || []) if (!String(g.label || g.city || "").trim()) errors.push("Each pickup point needs a name, e.g. Dimapur Airport.");
    for (const d of trip?.departures || []) if (!/^\d{4}-\d{2}-\d{2}$/.test(d?.start || "") || (d.end && !/^\d{4}-\d{2}-\d{2}$/.test(d.end))) errors.push("Fixed dates: write each date as YYYY-MM-DD.");
    return errors;
}
